import { useEffect, useState } from "react";
import { Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, money } from "@/lib/api";
import { RecurrencePicker, countVisits, Recurrence } from "@/components/dashboard/RecurrencePicker";

export function Payments() {
  const [packages, setPackages] = useState<any[]>([]);
  const [recipients, setRecipients] = useState<any[]>([]);
  const [currency, setCurrency] = useState<"gbp" | "usd">("gbp");
  const [recipientId, setRecipientId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [rec, setRec] = useState<Recurrence>(null);
  const [loadingKey, setLoadingKey] = useState("");

  useEffect(() => {
    api.get("/payments/packages").then((r) => setPackages(r.data));
    api.get("/recipients").then((r) => { setRecipients(r.data); if (r.data[0]) setRecipientId(r.data[0].recipient_id); });
  }, []);

  const pay = async (pkg: any) => {
    const price = pkg.prices.find((p: any) => p.currency === currency);
    if (rec && !date) return toast.error("Pick a start date for recurring visits");
    setLoadingKey(price.lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: price.lookup_key, origin_url: window.location.origin, recipient_id: recipientId || null, scheduled_at: date ? `${date}T${time}:00` : null, recurrence: rec });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err)); setLoadingKey(""); }
  };

  const field = "h-11 rounded-xl border border-brand-line bg-white px-3 text-sm outline-none ring-brand-blue/30 focus:ring-4";

  return (
    <section data-testid="payments-section">
      <div className="flex flex-col gap-4 rounded-2xl border border-brand-line bg-white p-6 shadow-subtle md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold text-brand-navy">Book a visit</h2>
          <p className="mt-1 text-sm text-brand-slate">Let our routing engine pick the best verified caregiver (Bhasa team first). Pay by card via Stripe. <span className="rounded-full bg-brand-goldSoft px-2 py-0.5 text-xs font-semibold text-amber-800" data-testid="stripe-test-badge">Test mode</span> Use card 4242 4242 4242 4242.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <select value={recipientId} onChange={(e) => setRecipientId(e.target.value)} className={field} data-testid="payment-recipient-select">
            <option value="">No recipient yet</option>
            {recipients.map((r) => <option key={r.recipient_id} value={r.recipient_id}>{r.full_name} · {r.city}</option>)}
          </select>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={field} data-testid="payment-date-input" />
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={field} data-testid="payment-time-input" />
          <div className="inline-flex rounded-xl border border-brand-line p-1" data-testid="currency-toggle">
            {(["gbp", "usd"] as const).map((c) => (
              <button key={c} onClick={() => setCurrency(c)} className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${currency === c ? "bg-brand-navy text-white" : "text-brand-slate"}`} data-testid={`currency-toggle-${c}`}>{c === "gbp" ? "£ GBP" : "$ USD"}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 max-w-xl"><RecurrencePicker value={rec} onChange={setRec} /></div>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        {packages.map((pkg) => {
          const price = pkg.prices.find((p: any) => p.currency === currency);
          const busy = loadingKey === price?.lookup_key;
          const visits = countVisits(date, rec) || 1;
          return (
            <article key={pkg.emergent_product_id} className="flex flex-col rounded-2xl border border-brand-line bg-white p-6 shadow-subtle transition hover:-translate-y-0.5 hover:shadow-lift" data-testid={`package-card-${pkg.emergent_product_id}`}>
              <div className="flex items-start justify-between"><h3 className="font-heading text-lg font-semibold text-brand-navy">{pkg.name}</h3><span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-brand-slate">{pkg.duration}</span></div>
              <p className="mt-2 flex-1 text-sm text-brand-slate">{pkg.description}</p>
              <div className="mt-5 flex items-center justify-between">
                <p className="font-heading text-2xl font-bold text-brand-navy" data-testid={`package-price-${pkg.emergent_product_id}`}>{price ? money(price.amount, price.currency) : "—"}{visits > 1 && <span className="block font-body text-xs font-medium text-brand-slate">× {visits} visits = {money(price.amount * visits, price.currency)}</span>}</p>
                <button onClick={() => pay(pkg)} disabled={!!loadingKey} className="inline-flex items-center gap-2 rounded-full bg-brand-emerald px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-emeraldHover disabled:opacity-60" data-testid={`pay-button-${pkg.emergent_product_id}`}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Pay into escrow
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
